import * as SelectPrimitive from '@radix-ui/react-select';
import { forwardRef, useState, type ReactNode } from 'react';
import { useFieldOptional } from './field-context';
import { IconCheck, IconChevronDown, IconChevronUp, IconPlus } from './icons';
import { cn, disabledStyles, focusRing } from './utils';

export interface SelectOption<T extends string = string> {
  readonly value: T;
  readonly label: string;
  readonly description?: string;
  readonly disabled?: boolean;
  /** Agrupa opções sob um cabeçalho. */
  readonly group?: string;
}

export interface SelectProps<T extends string = string> {
  readonly value: T | undefined;
  readonly onValueChange: (value: T) => void;
  readonly options: readonly SelectOption<T>[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly name?: string;
  readonly className?: string;
  readonly size?: 'sm' | 'md';
  /** Rótulo acessível quando o Select está fora de um `<Field>`. */
  readonly ariaLabel?: string;
  /**
   * CADASTRO RÁPIDO — o mesmo contrato do `Combobox`, pelo mesmo motivo.
   *
   * Aqui a entrada é só o botão ao lado do gatilho: o Select não tem campo de
   * busca, então não existe "termo digitado" para carregar nem lista vazia onde
   * oferecer a criação. `termo` vai vazio, e o diálogo abre em branco.
   *
   * O botão fica FORA do gatilho do Radix — um `<button>` dentro de outro é
   * HTML inválido, e o clique no interno abriria a lista em vez do cadastro.
   */
  readonly criar?: {
    readonly rotulo: string;
    readonly dialogo: (props: {
      readonly termo: string;
      readonly aoConcluir: (value: string) => void;
      readonly aoFechar: () => void;
    }) => ReactNode;
  };
}

/**
 * Select sobre Radix.
 *
 * Por que não `<select>` nativo: precisamos de descrição por opção (a política
 * de aprovação, por exemplo, exige explicar ANY_ONE × ALL_REQUIRED × QUORUM na
 * própria lista) e o `<option>` nativo não aceita marcação. O Radix entrega o
 * mesmo padrão de teclado do nativo — setas, Home/End, busca por digitação,
 * Esc para fechar — então não há perda de acessibilidade.
 */
export function Select<T extends string = string>({
  value,
  onValueChange,
  options,
  placeholder = 'Selecione…',
  disabled = false,
  name,
  className,
  size = 'md',
  ariaLabel,
  criar,
}: SelectProps<T>) {
  const field = useFieldOptional();
  const groups = groupOptions(options);
  const [criando, setCriando] = useState(false);

  const raiz = (
    <SelectPrimitive.Root
      /*
       * Sempre CONTROLADO, com string vazia significando "nada escolhido" — que
       * é como o próprio Radix a interpreta (`shouldShowPlaceholder`). Alternar
       * para `undefined` deixaria o componente oscilar entre não-controlado e
       * controlado a cada carga de dados, e o React avisa sobre isso.
       */
      value={value ?? ''}
      onValueChange={(next) => {
        /*
         * IGNORA O ECO VAZIO — não é escolha de ninguém.
         *
         * O Radix mantém um `<select>` nativo escondido para integração com
         * formulário, com `onChange: (e) => onValueChange(e.target.value)`, e um
         * efeito que dispara `change` nele sempre que o valor muda
         * (`SelectBubbleInput`). Quando o valor chega DEPOIS da montagem — todo
         * select preenchido por consulta — as `<option>` ainda não foram
         * registradas na coleção, então `select.value = "America/Sao_Paulo"` não
         * encontra opção correspondente, o navegador guarda `""`, e o `change`
         * devolve `""` para cá.
         *
         * Dentro de um `Controller` do react-hook-form isso gravava `""` por
         * cima do valor recém-carregado: a tela abria preenchida e ~15 ms depois
         * voltava para "Selecione…", sem um erro sequer no console.
         *
         * Nenhuma opção deste sistema tem valor vazio — o próprio Radix recusa
         * `<Item value="">` —, então `""` aqui só pode ser esse eco.
         */
        if (next === '') return;
        onValueChange(next as T);
      }}
      disabled={disabled}
      name={name}
    >
      <SelectPrimitive.Trigger
        {...field?.controlProps}
        aria-label={field ? undefined : ariaLabel}
        className={cn(
          'inline-flex w-full items-center justify-between gap-2 rounded-md border border-border-control bg-surface text-left text-content',
          size === 'md' ? 'h-[2.375rem] px-3 text-base' : 'h-8 px-2.5 text-sm',
          'data-[placeholder]:text-content-subtle',
          'aria-[invalid=true]:border-danger aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-danger',
          focusRing,
          disabledStyles,
          className,
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <IconChevronDown size={16} className="text-content-subtle" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            'z-dropdown max-h-[min(24rem,var(--radix-select-content-available-height))] w-[var(--radix-select-trigger-width)]',
            'overflow-hidden rounded-md border border-border bg-surface-raised shadow-lg',
            'animate-content-in',
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex h-6 items-center justify-center bg-surface-raised text-content-subtle">
            <IconChevronUp size={14} />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="p-1">
            {groups.map(([groupName, groupOptionsList]) => (
              <SelectPrimitive.Group key={groupName ?? '__ungrouped__'}>
                {groupName ? (
                  <SelectPrimitive.Label className="px-2 py-1.5 text-2xs font-semibold uppercase tracking-wide text-content-subtle">
                    {groupName}
                  </SelectPrimitive.Label>
                ) : null}
                {groupOptionsList.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    description={option.description}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectPrimitive.Group>
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex h-6 items-center justify-center bg-surface-raised text-content-subtle">
            <IconChevronDown size={14} />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );

  if (!criar) return raiz;

  return (
    <div className="flex items-center gap-1.5">
      <div className="min-w-0 flex-1">{raiz}</div>
      <button
        type="button"
        onClick={() => setCriando(true)}
        disabled={disabled}
        aria-label={criar.rotulo}
        title={criar.rotulo}
        className={cn(
          'flex h-[2.375rem] w-9 shrink-0 items-center justify-center rounded-md',
          'border border-border-control bg-surface text-content-subtle',
          'hover:border-brand hover:bg-brand-subtle hover:text-brand',
          focusRing,
          disabledStyles,
        )}
      >
        <IconPlus size={16} />
      </button>

      {criando
        ? criar.dialogo({
            termo: '',
            aoConcluir: (novoValor) => {
              onValueChange(novoValor as T);
              setCriando(false);
            },
            aoFechar: () => setCriando(false),
          })
        : null}
    </div>
  );
}

const SelectItem = forwardRef<
  HTMLDivElement,
  { value: string; disabled?: boolean; description?: string; children: ReactNode }
>(function SelectItem({ value, disabled, description, children }, ref) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      value={value}
      disabled={disabled}
      className={cn(
        'relative flex cursor-pointer select-none items-start gap-2 rounded-sm px-2 py-2 pr-8 text-base outline-none',
        'data-[highlighted]:bg-brand-subtle data-[highlighted]:text-content',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-55',
      )}
    >
      <div className="min-w-0 flex-1">
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
        {description ? (
          <p className="mt-0.5 text-xs text-content-subtle">{description}</p>
        ) : null}
      </div>
      <SelectPrimitive.ItemIndicator className="absolute right-2 top-2.5">
        <IconCheck size={15} className="text-brand" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});

function groupOptions<T extends string>(
  options: readonly SelectOption<T>[],
): Array<[string | null, SelectOption<T>[]]> {
  const map = new Map<string | null, SelectOption<T>[]>();
  for (const option of options) {
    const key = option.group ?? null;
    const bucket = map.get(key);
    if (bucket) bucket.push(option);
    else map.set(key, [option]);
  }
  return [...map.entries()];
}
